import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SITE_URL, canonical, seoCanonicalLink, seoOgUrl } from "@/lib/seo";

export const Route = createFileRoute("/privacy-policy")({
  component: PrivacyPolicyPage,
  head: () => ({
    meta: [
      { title: "Privacy Policy — SimpleTech" },
      { name: "description", content: "SimpleTech privacy policy. Learn how we collect, use, and protect your personal information." },
      { property: "og:title", content: "Privacy Policy — SimpleTech" },
      { property: "og:description", content: "Learn how SimpleTech protects your personal information." },
      seoOgUrl("/privacy-policy"),
    ],
    links: [seoCanonicalLink("/privacy-policy")],
  }),
});

function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SiteHeader />
      <main id="main-content" className="flex-1">
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-3xl mx-auto px-6">
            <p className="text-sm font-bold tracking-widest text-violet-700 uppercase mb-4">
              Legal
            </p>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight mb-8">
              Privacy Policy
            </h1>
            <p className="text-slate-500 text-sm mb-12">
              Last updated: August 24, 2026
            </p>

            <div className="prose prose-slate max-w-none">
              <p className="text-slate-700 leading-relaxed mb-6">
                SimpleTech ("we," "us," or "our") respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, disclose, and safeguard information collected through our website, IT support and managed services, consulting engagements, and communications with clients and website visitors.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">1. Information We Collect</h2>
              <p className="text-slate-700 leading-relaxed mb-4">We may collect the following types of information:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700 leading-relaxed mb-6">
                <li><strong>Contact Information:</strong> Name, email address, phone number, organization, job title, and other information submitted through our forms or communications.</li>
                <li><strong>Communications:</strong> Messages, inquiries, testimonials, and other content you choose to share with us.</li>
                <li><strong>Service & Engagement Information:</strong> Information submitted in connection with IT support requests, managed services onboarding, cybersecurity assessments, consultations, or related technology services.</li>
                <li><strong>Usage Data:</strong> IP address, browser type, pages visited, referring URLs, and other analytics data collected automatically.</li>
                <li><strong>Cookies:</strong> Small data files used to improve site functionality, enhance user experience, and analyze website traffic.</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">2. How We Use Your Information</h2>
              <p className="text-slate-700 leading-relaxed mb-4">We may use your information for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700 leading-relaxed mb-6">
                <li>To respond to your inquiries and provide requested IT services and support.</li>
                <li>To deliver managed IT services, cybersecurity solutions, network management, and related technology consulting.</li>
                <li>To respond to requests for information, consultations, pricing, or service proposals.</li>
                <li>To send updates, newsletters, or relevant technology and security information.</li>
                <li>To improve our website, services, and user experience.</li>
                <li>To comply with legal, regulatory, and data protection requirements.</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">3. Sharing of Information</h2>
              <p className="text-slate-700 leading-relaxed mb-4">We do not sell your personal information.</p>
              <p className="text-slate-700 leading-relaxed mb-4">
                We may share information with trusted third-party service providers who support our operations, such as website hosting, analytics, email communication, CRM, or marketing platforms.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                We may disclose information when legally required to do so or when necessary to protect our legal rights or comply with applicable laws and regulations.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">4. Data Security</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                We implement reasonable administrative, technical, and physical safeguards designed to protect your information. However, no method of transmission over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.
              </p>
              <p className="text-slate-700 leading-relaxed mb-6">
                While we take reasonable steps to protect information submitted through our website and services, users should avoid transmitting sensitive personal or confidential business information through unsecured email communications.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">5. Your Rights</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Depending on your location, you may have the right to access, correct, delete, or restrict the use of your personal information. To exercise these rights, please contact us using the information below.
              </p>
              <p className="text-slate-700 leading-relaxed mb-6">
                California residents may have additional privacy rights under applicable California privacy laws.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">6. Use of Testimonials</h2>
              <p className="text-slate-700 leading-relaxed mb-6">
                Testimonials shared with SimpleTech may be used on our website, marketing materials, or related communications with permission from the individual or organization providing the testimonial. We do not publish testimonials anonymously unless specifically requested.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">7. Third-Party Links</h2>
              <p className="text-slate-700 leading-relaxed mb-6">
                Our website may contain links to third-party websites or resources. We are not responsible for the privacy practices, content, or policies of those external sites.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">8. Children's Privacy</h2>
              <p className="text-slate-700 leading-relaxed mb-6">
                Our services are intended for businesses and professional organizations. We do not knowingly collect personal information from children under the age of 13.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">9. Changes to This Policy</h2>
              <p className="text-slate-700 leading-relaxed mb-6">
                We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last updated" date.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">10. SMS and Text Messaging</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                If you provide your mobile phone number and consent to receive text messages, SimpleTech may send you SMS messages related to appointment reminders, service updates, support status, billing notifications, and other communications directly related to your IT services or inquiry.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                <strong>Consent.</strong> We collect and record your consent before sending SMS messages. Website opt-in takes place on our <a href="/contact" className="text-sky-600 hover:text-sky-700 underline">Contact page</a>, where you enter your mobile number and voluntarily check the SMS consent checkbox (unchecked by default). Consent may also be obtained when you request service by phone or agree to receive text messages during onboarding. Consent to receive SMS messages is not required to purchase our services.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                <strong>Mobile information sharing.</strong> Mobile information will not be shared with third parties or affiliates for marketing or promotional purposes. Text messaging originator opt-in data and consent will not be shared with any third parties.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                <strong>Help.</strong> Reply HELP to any message for assistance, or contact us at <a href="tel:9092559112" className="text-sky-600 hover:text-sky-700 underline">(909) 255-9112</a>.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                <strong>Message frequency.</strong> Message frequency varies based on your service needs and may include automated or manual messages. You may receive a few messages per month or more during active support engagements.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                <strong>Opt-out.</strong> You can opt out of SMS communications at any time by replying STOP to any message, or by contacting us at <a href="mailto:contact@simpletechhelper.com" className="text-sky-600 hover:text-sky-700 underline">contact@simpletechhelper.com</a> or <a href="tel:9092559112" className="text-sky-600 hover:text-sky-700 underline">(909) 255-9112</a>. After opting out, you will receive a final confirmation message and no further SMS communications unless you opt back in.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                <strong>Rates and delivery.</strong> Message and data rates may apply. Carriers are not liable for delayed or undelivered messages. Delivery is not guaranteed and depends on your carrier and device availability.
              </p>
              <p className="text-slate-700 leading-relaxed mb-6">
                For additional terms governing SMS communications, please see our <a href="/terms-and-conditions" className="text-sky-600 hover:text-sky-700 underline">Terms and Conditions</a>.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">11. Contact Us</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                If you have questions about this Privacy Policy, please contact us:
              </p>
              <address className="not-italic text-slate-700 leading-relaxed mb-2">
                <strong>SimpleTech</strong><br />
                8880 N Benson Ave #121<br />
                Montclair, CA 91763
              </address>
              <p className="text-slate-700 leading-relaxed mb-1">
                Email: <a href="mailto:contact@simpletechhelper.com" className="text-sky-600 hover:text-sky-700 underline">contact@simpletechhelper.com</a>
              </p>
              <p className="text-slate-700 leading-relaxed">
                Phone: <a href="tel:9092559112" className="text-sky-600 hover:text-sky-700 underline">(909) 255-9112</a>
              </p>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
